<script setup lang="ts">
import { ref, computed } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: number;
    starCount?: number;
    changeable?: boolean;
    changeStep?: number;
  }>(),
  {
    modelValue: 0,
    starCount: 5,
    changeable: false,
    changeStep: 0.5,
  }
);
const emit = defineEmits<{
  (e: 'update:model-value', modelValue: number): void;
}>();

const hoveredValue = ref<number>();
const ratingWidth = computed(() => {
  const MAX_PERCENT = 100;
  const value = hoveredValue.value ?? props.modelValue;
  return (value / props.starCount) * MAX_PERCENT;
});
const ratingRange = computed(() => {
  const range: number[] = [];
  for (let i = -1; i <= 0; i += props.changeStep) range.push(i);
  return range;
});

function highlightRating(value: number) {
  if (!props.changeable) return;
  hoveredValue.value = value;
}
function unhighlightRating() {
  hoveredValue.value = undefined;
}
function setRating(value: number) {
  if (!props.changeable) return;
  emit('update:model-value', value);
}
function generateEvents(value: number) {
  return {
    onmouseover: () => highlightRating(value),
    onmouseleave: () => unhighlightRating(),
    onclick: () => setRating(value),
  };
}
</script>

<template>
  <div :class="[$style.rating, { rating_changeable: props.changeable }]">
    <span
      v-for="index in props.starCount"
      :key="index"
      :class="$style.ratingStar"
    >
      <span
        v-for="subIndex in ratingRange"
        :key="subIndex"
        v-bind="generateEvents(index + subIndex)"
      ></span>
    </span>
    <div
      :class="[
        $style.rating,
        $style.ratingFilled,
        { rating_hovered: hoveredValue !== undefined },
      ]"
      :style="{ width: `${ratingWidth}%` }"
    >
      <span
        v-for="index in props.starCount"
        :key="index"
        :class="[$style.ratingStar, $style.ratingFilled]"
      >
        <span
          v-for="subIndex in ratingRange"
          :key="subIndex"
          v-bind="generateEvents(index + subIndex)"
        ></span>
      </span>
    </div>
  </div>
</template>

<style module>
.rating {
  position: relative;
  display: inline-flex;

  &.ratingChangeable {
    cursor: pointer;
  }

  &.ratingFilled {
    display: inline-flex;
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    overflow: hidden;
  }

  &.ratingHovered {
    opacity: 50%;
  }

  .ratingStar {
    display: flex;
    width: 1.5em;
    height: 1.5em;
    flex-shrink: 0;
    background: url("data:image/svg+xml, %3Csvg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.1442 4.42382C11.5328 3.778 12.4691 3.778 12.8578 4.42382L14.745 7.55966C14.8846 7.79167 15.1124 7.95714 15.3762 8.01824L18.9417 8.84403C19.676 9.01409 19.9653 9.90456 19.4712 10.4738L17.072 13.2376C16.8945 13.4421 16.8075 13.7098 16.8309 13.9796L17.1474 17.6258C17.2126 18.3767 16.4551 18.9271 15.761 18.6331L12.3911 17.2054C12.1417 17.0997 11.8602 17.0997 11.6109 17.2054L8.24094 18.6331C7.5469 18.9271 6.78942 18.3767 6.85459 17.6258L7.17104 13.9796C7.19445 13.7098 7.10746 13.4421 6.92995 13.2376L4.53075 10.4738C4.03664 9.90456 4.32598 9.01409 5.06029 8.84403L8.62582 8.01824C8.88963 7.95714 9.11738 7.79167 9.257 7.55966L11.1442 4.42382Z' stroke='%23FFC839' stroke-width='1.5' /%3E%3C/svg%3E")
      no-repeat;

    span {
      width: 100%;
    }

    &.ratingFilled {
      background: url("data:image/svg+xml, %3Csvg viewBox='0 0 24 24' fill='%23FFC839' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.1442 4.42382C11.5328 3.778 12.4691 3.778 12.8578 4.42382L14.745 7.55966C14.8846 7.79167 15.1124 7.95714 15.3762 8.01824L18.9417 8.84403C19.676 9.01409 19.9653 9.90456 19.4712 10.4738L17.072 13.2376C16.8945 13.4421 16.8075 13.7098 16.8309 13.9796L17.1474 17.6258C17.2126 18.3767 16.4551 18.9271 15.761 18.6331L12.3911 17.2054C12.1417 17.0997 11.8602 17.0997 11.6109 17.2054L8.24094 18.6331C7.5469 18.9271 6.78942 18.3767 6.85459 17.6258L7.17104 13.9796C7.19445 13.7098 7.10746 13.4421 6.92995 13.2376L4.53075 10.4738C4.03664 9.90456 4.32598 9.01409 5.06029 8.84403L8.62582 8.01824C8.88963 7.95714 9.11738 7.79167 9.257 7.55966L11.1442 4.42382Z' stroke='%23FFC839' stroke-width='1.5' /%3E%3C/svg%3E")
        no-repeat;
    }
  }
}
</style>
